/**
 * Channel Detail Page.
 *
 * Shows channel config, status, and for Teams channels:
 *   - JWKS cache status badge (timestamp of last fetch)
 *   - "Refresh JWKS" button → POST /channels/{id}/refresh-jwks
 */

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { Button, Card, Badge } from '@/components/ui';
import { useChannel, useRefreshJwks, useUpdateChannel } from '@/features/channels';
import { toast } from 'sonner';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// =============================================================================
// JWKS Cache Status (Teams only)
// =============================================================================

function JwksCacheStatus({ channelId, cachedAt }: { channelId: string; cachedAt: string | null | undefined }) {
  const refreshMutation = useRefreshJwks();

  const handleRefresh = async () => {
    try {
      await refreshMutation.mutateAsync(channelId);
      toast.success('JWKS cache refreshed');
    } catch {
      toast.error('Failed to refresh JWKS cache');
    }
  };

  const isStale = !cachedAt;

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Bot Framework JWKS Cache</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Public keys used to verify incoming Bot Framework JWT tokens (RS256).
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshMutation.isPending}
          title="Invalidate and refresh JWKS cache"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          Refresh JWKS
        </Button>
      </div>

      <div className="mt-3 flex items-center gap-2">
        {isStale ? (
          <>
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />
            <span className="text-xs text-yellow-600 dark:text-yellow-400">Not yet cached — will be fetched on first inbound message</span>
          </>
        ) : (
          <>
            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
            <span className="text-xs text-green-700 dark:text-green-400">Cached</span>
            <Clock className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-gray-500">
              Last fetched {relativeTime(cachedAt!)}
            </span>
          </>
        )}
      </div>
    </Card>
  );
}

// =============================================================================
// Page
// =============================================================================

export function ChannelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: channel, isLoading } = useChannel(id!);
  const updateMutation = useUpdateChannel(id!);

  const handleToggleStatus = async () => {
    if (!channel) return;
    const newStatus = channel.status === 'active' ? 'paused' : 'active';
    try {
      await updateMutation.mutateAsync({ status: newStatus });
      toast.success(`Channel ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch {
      toast.error('Failed to update channel status');
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-gray-400">Loading…</div>;
  }

  if (!channel) {
    return <div className="p-8 text-center text-gray-500">Channel not found.</div>;
  }

  const isTeams = channel.channel_type === 'teams';

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/channels')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Channels
        </Button>
        <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{channel.display_name ?? channel.name}</h1>
        <Badge
          variant={
            channel.status === 'active'
              ? 'secondary'
              : channel.status === 'error'
                ? 'destructive'
                : channel.status === 'pending_verification' || channel.status === 'paused'
                  ? 'outline'
                  : 'default'
          }
        >
          {channel.status}
        </Badge>
      </div>

      {/* Summary */}
      <Card className="p-4 grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-xs text-gray-400 mb-0.5">Type</p>
          <p className="font-medium text-gray-800 dark:text-gray-200 capitalize">{channel.channel_type}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-0.5">Target</p>
          <p className="font-medium text-gray-800 dark:text-gray-200">{channel.target_type}:{channel.target_id}</p>
        </div>
        <div className="col-span-2">
          <p className="text-xs text-gray-400 mb-0.5">Webhook URL</p>
          <code className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
            {channel.webhook_url}
          </code>
        </div>
      </Card>

      {/* Teams-specific: JWKS */}
      {isTeams && (
        <JwksCacheStatus
          channelId={channel.id}
          cachedAt={channel.jwks_cached_at}
        />
      )}

      {/* Config */}
      {Object.keys(channel.config).length > 0 && (
        <Card className="p-4">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">Configuration</p>
          <dl className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(channel.config).map(([k, v]) => (
              <div key={k}>
                <dt className="text-gray-400">{k}</dt>
                <dd className="font-medium text-gray-700 dark:text-gray-300 truncate">
                  {String(v)}
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleToggleStatus}
          disabled={updateMutation.isPending}
        >
          {channel.status === 'active' ? 'Pause Channel' : 'Activate Channel'}
        </Button>
      </div>
    </div>
  );
}
